<template>
  <div class="le-view">

    <!-- ─── Waiting banner ─── -->
      <div 
        v-if="location && location.raw && location.raw.metadata && location.raw.metadata.hasPendingEdits"
        class="le-pending-banner"
      >
        <i class="material-icons">info</i>
        <span>{{ $t('dash.presenceManagement.location.pendingEdits') }}</span>
      </div>

    <!-- ─── Top bar ─── -->
    <div class="le-topbar">
      <vs-button
        color="#6a1f4c"
        type="filled"
        class="le-save-btn"
        @click="handleSaveClick"
      >
        {{ $t('dash.presenceManagement.location.save') }}
      </vs-button>
      <vs-button
        color="#6a1f4c"
        type="border"
        class="le-back-btn"
        @click="$router.push({ name: 'presence-management' })"
      >
        {{ $t('dash.presenceManagement.hours.back') }}
      </vs-button>
    </div>

    <!-- ─── Hero ─── -->
    <div v-if="location && localData" class="le-hero">
      <!-- Photo + identity -->
      <div class="le-hero-identity">
        <div class="le-hero-photo-wrapper">
          <img
            v-if="heroCoverUrl && !coverError"
            :src="heroCoverUrl"
            class="le-hero-photo"
            :class="{ 'le-hero-photo--loaded': isCoverLoaded }"
            alt="Cover"
            referrerpolicy="no-referrer"
            loading="eager"
            @load="isCoverLoaded = true"
            @error="coverError = true"
          />
          <div v-if="heroCoverUrl && !isCoverLoaded && !coverError" class="le-hero-photo-loading-skeleton"></div>
          <div v-if="currentNetwork !== 'instagram'" class="le-cover-upload-trigger btn-add-media" @click="triggerCoverUpload">
            <i class="material-icons">photo_camera</i>
            <span>{{ $t('dash.presenceManagement.location.media.changeCover') }}</span>
          </div>
          <input
            type="file"
            ref="coverFileInput"
            accept="image/*"
            style="display: none"
            @change="handleCoverUpload"
          />
        </div>
        <div class="le-hero-meta-container">
          <div class="le-hero-logo-wrapper">
            <div class="le-hero-logo" :style="heroLogoStyle"></div>
            <div class="le-logo-upload-trigger btn-add-media" @click="triggerLogoUpload">
              <i class="material-icons">photo_camera</i>
            </div>
            <input
              type="file"
              ref="logoFileInput"
              accept="image/*"
              style="display: none"
              @change="handleLogoUpload"
            />
            <input
              type="file"
              ref="additionalPhotoFileInput"
              accept="image/*"
              style="display: none"
              @change="handleAdditionalPhotoUpload"
            />
          </div>
          <component
            :is="externalNetworkUri ? 'a' : 'div'"
            :href="externalNetworkUri || undefined"
            target="_blank"
            class="le-hero-meta"
            :class="{ 'le-hero-meta--link': !!externalNetworkUri }"
            :title="$t('dash.presenceManagement.location.hero.viewListing')"
          >
            <p class="le-hero-name">{{ localData.title }}</p>
            <p class="le-hero-address">{{ heroAddress }}</p>
          </component>

          <div class="le-hero-network-badge" v-if="networkBadgeIcon">
            <img :src="networkBadgeIcon" :alt="currentNetwork" class="le-hero-network-icon" />
            <img
              v-if="currentNetwork === 'google'"
              :src="wazeBadgeIcon"
              alt="Waze"
              class="le-hero-network-icon"
              title="Waze"
            />
          </div>
        </div>
      </div>

      <!-- Completion score -->
      <div class="le-hero-score">
        <completion-donut :value="completionAudit.score" :band="completionAudit.band" />
        <div class="le-hero-score-text">
          <p class="le-hero-score-title">{{ $t('dash.presenceManagement.location.profile.scorePanel.title') }}</p>
          <p class="le-hero-score-subtitle">{{ $t('dash.presenceManagement.location.hero.scoreSubtitle') }}</p>
          
          <div class="le-hero-score-badges" v-if="missingCount > 0 || ((currentNetwork === 'google' || currentNetwork === 'facebook') && unansweredReviewsCount > 0)">
            <div v-if="missingCount > 0" class="le-hero-score-missing-wrapper">
              <p class="le-hero-score-missing">
                <i class="material-icons le-missing-icon">info_outline</i>
                {{ missingCount }} {{ $t('dash.presenceManagement.location.hero.missing') }}
              </p>

              <!-- Hover Popover Tooltip -->
              <div class="le-score-popover">
                <p class="le-score-popover-title">{{ $t('dash.presenceManagement.score.popover.title') }}</p>
                <ul class="le-score-popover-list">
                  <li
                    v-for="item in missingItems"
                    :key="item.key"
                    class="le-score-popover-item"
                    @click="activeTab = item.tab"
                  >
                    <span class="le-score-popover-label">{{ $te(item.labelKey) ? $t(item.labelKey) : item.defaultLabel }}</span>
                    <small class="le-score-popover-weight">+{{ item.weight }}%</small>
                    <span class="le-score-popover-btn">{{ $t('dash.presenceManagement.score.popover.action') }}</span>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Pending Reviews Badge (GBP & Facebook) -->
            <div v-if="(currentNetwork === 'google' || currentNetwork === 'facebook') && unansweredReviewsCount > 0" class="le-hero-score-pending-wrapper">
              <p class="le-hero-score-pending" @click="activeTab = 'reviews'" :title="$t('dash.presenceManagement.location.hero.goToReviews')">
                <i class="material-icons le-pending-icon">{{ currentNetwork === 'facebook' ? 'thumb_up' : 'star' }}</i>
                {{ unansweredReviewsCount }} {{ currentNetwork === 'facebook' ? $t('dash.presenceManagement.reviews.heroPendingNoticeFacebook') : $t('dash.presenceManagement.reviews.heroPendingNotice') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ─── Tabs + content card ─── -->
    <div class="le-content-card">

      <div class="le-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="le-tab"
          :class="{ 'le-tab--active': activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- Tab content -->
      <div v-if="localData" class="le-tab-content">

        <!-- Tab: Horaires -->
        <div v-show="activeTab === 'hours'">
          <template v-if="isFacebookWithoutAddress">
            <div class="le-facebook-no-address-banner">
              <div class="le-fb-no-addr-header">
                <i class="material-icons le-fb-no-addr-icon">location_off</i>
                <h3 class="le-fb-no-addr-title">{{ $t('dash.presenceManagement.location.hours.facebookNoAddressTitle') }}</h3>
              </div>
              <p class="le-fb-no-addr-text">
                {{ $t('dash.presenceManagement.location.hours.facebookNoAddressText') }}
              </p>
              <button class="le-fb-no-addr-btn" @click="activeTab = 'profile'">
                <span>{{ $t('dash.presenceManagement.location.hours.facebookNoAddressAction') }}</span>
              </button>
            </div>
          </template>
          <template v-else>
            <tab-hours
              ref="tabHours"
              :periods="localData.regularHours && localData.regularHours.periods || []"
            />
          </template>
        </div>

        <!-- Tab: Jours fériés -->
        <div v-show="activeTab === 'specialHours'">
          <tab-special-hours
            ref="tabSpecialHours"
            :special-hour-periods="localData.specialHours && localData.specialHours.specialHourPeriods || []"
          />
        </div>

        <!-- Tab: Activité -->
        <div v-show="activeTab === 'activity'">
          <tab-activity
            ref="tabActivity"
            :location-name="localData.name"
            :categories="localData.categories || {}"
            :gbp-attributes="localData.attributes || []"
            :service-items="localData.serviceItems || []"
            :is-service-area-only="isServiceAreaOnly"
            :supported-attributes="localData.supportedAttributes || []"
            :location-data="localData"
            :current-network="currentNetwork"
          />
        </div>

        <!-- Tab: Profil -->
        <div v-show="activeTab === 'profile'">
          <tab-profile
            ref="tabProfile"
            :location-data="localData"
            :completion-score="location ? location.completionScore : 0"
            :completion-band="location ? location.completionBand : 'low'"
            @add-photo="triggerAdditionalPhotoUpload"
            @switch-tab="activeTab = $event"
          />
        </div>

        <!-- Tab: Avis / Recommandations (GBP & Facebook) -->
        <div v-show="activeTab === 'reviews'" v-if="currentNetwork === 'google' || currentNetwork === 'facebook'">
          <tab-reviews
            ref="tabReviews"
            :location-data="localData"
            :location-id="resolvedLocationId"
            :current-network="currentNetwork"
          />
        </div>

      </div>

      <!-- Empty state: location not found -->
      <div v-else-if="notFound" class="le-not-found">
        <p>{{ $t('dash.presenceManagement.location.notFound') }}</p>
        <vs-button color="#6a1f4c" type="border" @click="$router.push({ name: 'presence-management' })">
          {{ $t('dash.presenceManagement.hours.back') }}
        </vs-button>
      </div>

    </div>

    <!-- ─── Image Cropper Overlay ─── -->
    <portal to="dash" v-if="cropper.visible && cropper.src">
      <ImageCropper
        :src="cropper.src"
        :imageState="cropper.imageState"
        :allowed-networks="['google', 'instagram', 'facebook']"
        @clickOutside="cropper.visible = false"
        @finished="onCropperFinished"
      />
    </portal>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { computeCompletionAudit } from '@/store/modules/presenceManagement'
import { EventBus } from '@/event-bus'
import axios from 'axios'
import CompletionDonut from './components/ui-core/CompletionDonut.vue'
import TabHours from './sub-tabs/TabHours.vue'
import TabSpecialHours from './sub-tabs/TabSpecialHours.vue'
import TabActivity from './sub-tabs/TabActivity.vue'
import TabProfile from './sub-tabs/TabProfile.vue'
import TabReviews from './sub-tabs/TabReviews.vue'
import ImageCropper from '@/components/dash/ImageCropper.vue'

export default {
  name: 'location-editor',

  components: { CompletionDonut, TabHours, TabSpecialHours, TabActivity, TabProfile, TabReviews, ImageCropper },

  data() {
    const validTabs = ['profile', 'hours', 'specialHours', 'activity', 'reviews']
    const queryTab = this.$route?.query?.tab
    const initialTab = (queryTab && validTabs.includes(queryTab)) ? queryTab : 'profile'

    return {
      activeTab: initialTab,
      location: null,
      localData: null,
      notFound: false,
      unansweredReviewsCount: 0,
      isCoverLoaded: false,
      coverError: false,
      cropper: {
        visible: false,
        src: null,
        type: null,
        imageState: null
      }
    }
  },

  computed: {
    ...mapGetters('presenceManagement', ['locationById']),

    currentNetwork() {
      const net = this.localData?.network || (Array.isArray(this.localData?.networks) && this.localData.networks[0]) || this.location?.network || 'google'
      return String(net).toLowerCase()
    },

    resolvedLocationId() {
      return (this.location && (this.location.id || this.location.name)) || (this.localData && (this.localData.id || this.localData.name)) || ''
    },

    networkBadgeIcon() {
      switch (this.currentNetwork) {
        case 'facebook':
          return require('@/assets/img/iconsNetwork/ico-facebook-2x.png')
        case 'instagram':
          return require('@/assets/img/iconsNetwork/ico-instagram-2x.png')
        case 'apple':
          return require('@/assets/img/iconsNetwork/ico-apple-2x.png')
        case 'google':
        default:
          return require('@/assets/img/iconsNetwork/ico-google-2x.png')
      }
    },

    wazeBadgeIcon() {
      return require('@/assets/img/iconsNetwork/ico-waze-2x.png')
    },

    tabs() {
      const allTabs = [
        { key: 'profile',      label: this.$t('dash.presenceManagement.location.tabs.profile') },
        { key: 'hours',        label: this.$t('dash.presenceManagement.location.tabs.hours') },
        { key: 'specialHours', label: this.$t('dash.presenceManagement.location.tabs.specialHours') },
        { key: 'activity',     label: this.$t('dash.presenceManagement.location.tabs.activity') },
      ]

      if (this.currentNetwork === 'instagram') {
        return allTabs.filter(t => t.key === 'profile' || t.key === 'activity')
      }
      if (this.currentNetwork === 'facebook') {
        const fbTabs = allTabs.filter(t => t.key !== 'specialHours')
        fbTabs.push({ key: 'reviews', label: this.$t('dash.presenceManagement.location.tabs.recommendations') })
        return fbTabs
      }

      if (this.currentNetwork === 'google') {
        allTabs.push({ key: 'reviews', label: this.$t('dash.presenceManagement.location.tabs.reviews') })
      }

      return allTabs
    },

    completionAudit() {
      if (!this.localData) return { score: 0, band: 'low', missingItems: [], missingCount: 0 }
      return computeCompletionAudit(this.localData)
    },

    missingItems() {
      return this.completionAudit.missingItems || []
    },

    missingCount() {
      return this.completionAudit.missingCount || 0
    },

    storeLocation() {
      return this.locationById(this.$route.params.id)
    },

    heroAddress() {
      const a = this.localData?.storefrontAddress
      if (!a) return ''
      const lines = (a.addressLines || []).join(' ')
      return [lines, [a.postalCode, a.locality].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    },

    heroLogoStyle() {
      const logo = this.location?.avatar || this.location?.imageUrl || this.localData?.avatar || ''
      return logo ? `background-image: url('${logo}')` : ''
    },

    heroCoverUrl() {
      let cover = ''
      if (this.currentNetwork === 'instagram') {
        cover = this.location?.avatar || this.location?.imageUrl || this.localData?.avatar || ''
      } else {
        cover = this.location?.coverPhoto || this.location?.coverImageUrl || this.localData?.coverPhoto || ''
      }
      if (!cover || typeof cover !== 'string') return ''
      let cleanUrl = cover.trim().replace(/^http:\/\//i, 'https://')
      if (cleanUrl.includes('googleusercontent.com')) {
        if (cleanUrl.match(/=[swh]\d+/)) {
          cleanUrl = cleanUrl.replace(/=[swh]\d+[^=]*$/, '=w1000-h300-c')
        } else {
          cleanUrl = `${cleanUrl}=w1000-h300-c`
        }
      }
      return cleanUrl
    },

    heroCoverStyle() {
      const url = this.heroCoverUrl
      return url ? `background-image: url('${url}')` : ''
    },

    externalNetworkUri() {
      if (!this.localData) return ''
      const net = this.currentNetwork
      if (net === 'facebook') {
        const fbUrl = this.location?.facebookUri || this.localData?.facebookUri || this.localData?.socialProfiles?.facebook || this.localData?.websiteUri
        if (fbUrl) return fbUrl.startsWith('http') ? fbUrl : `https://${fbUrl}`
        return 'https://facebook.com'
      }
      if (net === 'instagram') {
        const igUrl = this.location?.instagramUri || this.localData?.instagramUri || this.localData?.socialProfiles?.instagram
        if (igUrl) return igUrl.startsWith('http') ? igUrl : `https://${igUrl}`
        const handle = this.localData?.username || this.localData?.handle || this.location?.username || ''
        if (handle) return `https://instagram.com/${handle.replace(/^@/, '')}`
        if (this.localData?.websiteUri) {
          return this.localData.websiteUri.startsWith('http') ? this.localData.websiteUri : `https://${this.localData.websiteUri}`
        }
        return 'https://instagram.com'
      }
      return this.localData?.metadata?.mapsUri || ''
    },

    isServiceAreaOnly() {
      return this.localData?.serviceArea?.businessType === 'CUSTOMER_LOCATION_ONLY'
    },

    isFacebookWithoutAddress() {
      if (this.currentNetwork !== 'facebook') return false
      const a = this.localData?.storefrontAddress
      if (!a) return true
      const hasStreet = a.addressLines && a.addressLines.length > 0 && !!a.addressLines[0].trim()
      const hasCity = a.locality && !!a.locality.trim()
      return !(hasStreet || hasCity)
    },
  },

  watch: {
    '$route.query.tab': {
      immediate: true,
      handler(newTab) {
        if (newTab && ['profile', 'hours', 'specialHours', 'activity'].includes(newTab)) {
          this.activeTab = newTab
        }
      }
    },
    storeLocation: {
      immediate: true,
      handler(newLocation) {
        if (newLocation) {
          this.location = newLocation
          this.localData = JSON.parse(JSON.stringify(newLocation.raw))
          this.notFound = false
          this.isCoverLoaded = false
          this.coverError = false
          this.loadUnansweredReviewsCount()
        } else if (this.$store.state.presenceManagement.locations.length > 0) {
          this.notFound = true
        }
      }
    }
  },

  created() {
    EventBus.$emit('selectPageSection/freezeSelectPage', true)
    if (!this.$store.state.presenceManagement.locations.length) {
      this.$store.dispatch('presenceManagement/fetchLocations')
    }
  },

  mounted() {
    EventBus.$emit('selectPageSection/freezeSelectPage', true)
  },

  beforeDestroy() {
    EventBus.$emit('selectPageSection/freezeSelectPage', false)
  },

  methods: {
    ...mapActions('presenceManagement', ['saveLocationData', 'fetchLocationReviews']),

    async loadUnansweredReviewsCount() {
      if (this.currentNetwork !== 'google' && this.currentNetwork !== 'facebook') return
      const targetId = this.location?.id || this.location?.name || this.localData?.id || this.localData?.name
      if (!targetId) return
      try {
        const res = await this.fetchLocationReviews({ locationId: targetId })
        if (res && res.success && Array.isArray(res.reviews)) {
          this.unansweredReviewsCount = res.reviews.filter(r => !r.reviewReply).length
        }
      } catch (e) {
        console.error('Error fetching unanswered reviews count:', e)
      }
    },

    handleSaveClick() {
      if (this.$refs.tabHours && !this.$refs.tabHours.validate()) return
      if (this.$refs.tabProfile && !this.$refs.tabProfile.validate()) return

      EventBus.$emit(
        'showModal',
        this.$t('dash.presenceManagement.location.confirm.title'),
        this.$t('dash.presenceManagement.location.confirm.message'),
        this.$t('dash.presenceManagement.hours.confirm.accept'),
        this.$t('dash.presenceManagement.hours.confirm.cancel'),
        () => { this.onConfirmSave() }
      )
    },

    async onConfirmSave() {
      const scheduleData    = this.$refs.tabHours       ? this.$refs.tabHours.getScheduleData()           : {}
      const specialHoursData = this.$refs.tabSpecialHours ? this.$refs.tabSpecialHours.getSpecialHoursData() : {}
      const activityData    = this.$refs.tabActivity    ? this.$refs.tabActivity.getActivityData()        : {}
      const profileData     = this.$refs.tabProfile     ? this.$refs.tabProfile.getProfileData()          : {}

      const updatedRaw = {
        ...this.localData,
        title:         profileData.title !== undefined ? profileData.title : this.localData.title,
        storefrontAddress: profileData.storefrontAddress || this.localData.storefrontAddress,
        phoneNumbers:  profileData.phoneNumbers || this.localData.phoneNumbers,
        regularHours:  { periods: scheduleData.periods || [] },
        specialHours:  { specialHourPeriods: specialHoursData.specialHourPeriods || [] },
        categories:    activityData.categories    || this.localData.categories,
        attributes:    activityData.attributes    || this.localData.attributes,
        profile:       profileData.profile        || this.localData.profile,
        websiteUri:    profileData.websiteUri     !== undefined ? profileData.websiteUri : this.localData.websiteUri,
        socialProfiles: profileData.socialProfiles || this.localData.socialProfiles,
        email:            profileData.email !== undefined ? profileData.email : this.localData.email,
        whatsappNumber:   profileData.whatsappNumber !== undefined ? profileData.whatsappNumber : this.localData.whatsappNumber,
        foundingDate:     profileData.foundingDate !== undefined ? profileData.foundingDate : this.localData.foundingDate,
        privacyPolicyUrl: profileData.privacyPolicyUrl !== undefined ? profileData.privacyPolicyUrl : this.localData.privacyPolicyUrl,
        impressum:        profileData.impressum !== undefined ? profileData.impressum : this.localData.impressum,
        customLinks:      profileData.customLinks || this.localData.customLinks,
        priceRange:       activityData.priceRange || this.localData.priceRange,
        specialties:      activityData.specialties || this.localData.specialties
      }

      this.$vs.loading({
        scale: 0.8,
        text: this.$t('dash.presenceManagement.location.saving')
      })

      try {
        const result = await this.saveLocationData({ locationId: this.location.id, rawData: updatedRaw })

        if (result && (result === true || result.success)) {
          this.$vs.notify({
            title: this.$t('dash.presenceManagement.location.saveSuccess.title'),
            text:  this.$t('dash.presenceManagement.location.saveSuccess.text'),
            color: '#6a1f4c',
            position: 'top-right'
          })
          this.$router.push({ name: 'presence-management', query: { saved: 'true' } })
        } else {
          const errorMsg = (result && result.error) || this.$t('dash.presenceManagement.location.error.saveFailed')
          this.$vs.notify({
            title: this.$t('dash.presenceManagement.location.error.title'),
            text: errorMsg,
            color: 'danger',
            position: 'top-right'
          })
        }
      } catch (err) {
        console.error('Error saving location:', err)
        this.$vs.notify({
          title: this.$t('dash.presenceManagement.location.error.title'),
          text: this.$t('dash.presenceManagement.location.error.generic'),
          color: 'danger',
          position: 'top-right'
        })
      } finally {
        this.$vs.loading.close()
      }
    },

    triggerCoverUpload() {
      this.$refs.coverFileInput.click()
    },

    triggerLogoUpload() {
      this.$refs.logoFileInput.click()
    },

    triggerAdditionalPhotoUpload() {
      this.$refs.additionalPhotoFileInput.click()
    },

    handleCoverUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      this.openCropper(file, 'COVER')
      event.target.value = ''
    },

    handleLogoUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      this.openCropper(file, 'LOGO')
      event.target.value = ''
    },

    handleAdditionalPhotoUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      this.openCropper(file, 'ADDITIONAL')
      event.target.value = ''
    },

    openCropper(file, type) {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.cropper.src = e.target.result
        this.cropper.type = type
        this.cropper.imageState = null
        this.cropper.visible = true
      }
      reader.readAsDataURL(file)
    },

    async onCropperFinished({ dest }) {
      this.cropper.visible = false
      await this.uploadLocationImage(dest, this.cropper.type)
      this.cropper.src = null
      this.cropper.type = null
    },

    async uploadLocationImage(file, category) {
      this.$vs.loading({
        container: '.le-hero-identity',
        scale: 0.6,
        text: category === 'LOGO' 
          ? this.$t('dash.presenceManagement.location.media.updatingLogo') 
          : category === 'COVER'
            ? this.$t('dash.presenceManagement.location.media.updatingCover')
            : this.$t('dash.presenceManagement.location.media.addingPhoto')
      })

      try {
        // 1. Get signed S3 upload URL from engine backend
        const responseUrl = await this.$http.get(
          `${process.env.VUE_APP_API_HOST}/tools/get-upload-url?fileName=${encodeURIComponent(file.name)}&fileType=${encodeURIComponent(file.type)}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        )

        const uploadData = responseUrl.body
        if (!uploadData || !uploadData.uploadURL) {
          throw new Error('Could not get upload URL')
        }

        // 2. Upload the file to S3 directly
        await axios.put(uploadData.uploadURL, file, {
          headers: {
            'Content-Type': file.type
          }
        })

        // 3. Register the media with GMB backend
        const cleanLocationId = this.location.id.replace('locations/', '')
        const responseMedia = await this.$http.post(
          `${process.env.VUE_APP_API_HOST}/presence-management/locations/${cleanLocationId}/media`,
          {
            mediaUrl: uploadData.urlFinal,
            category: category
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        )

        if (responseMedia.body.success) {
          const notifyText = category === 'LOGO' 
            ? this.$t('dash.presenceManagement.location.media.logoSuccess') 
            : category === 'COVER'
              ? this.$t('dash.presenceManagement.location.media.coverSuccess')
              : this.$t('dash.presenceManagement.location.media.photoSuccess')

          this.$vs.notify({
            title: this.$t('dash.presenceManagement.location.saveSuccess.title'),
            text: notifyText,
            color: '#6a1f4c',
            position: 'top-right'
          })

          // Update local preview immediately
          const finalUrl = uploadData.urlFinal || uploadData.fileURL
          if (category === 'LOGO') {
            this.location.imageUrl = finalUrl
            if (this.localData) this.localData.avatar = finalUrl
          } else if (category === 'COVER') {
            this.location.coverImageUrl = finalUrl
            if (this.localData) this.localData.coverPhoto = finalUrl
          } else {
            // ADDITIONAL photo
            if (!this.location.additionalPhotos) {
              this.$set(this.location, 'additionalPhotos', [])
            }
            this.location.additionalPhotos.push(finalUrl)

            if (this.localData) {
              if (!this.localData.additionalPhotos) {
                this.$set(this.localData, 'additionalPhotos', [])
              }
              this.localData.additionalPhotos.push(finalUrl)
            }
          }

          // Trigger a refresh of the locations list in the store
          this.$store.dispatch('presenceManagement/fetchLocations')
        } else {
          throw new Error(responseMedia.body.message || 'Failed to link media to GMB')
        }

      } catch (error) {
        console.error('Error uploading image:', error)
        this.$vs.notify({
          title: this.$t('dash.presenceManagement.location.error.title'),
          text: this.$t('dash.presenceManagement.location.media.uploadFailed'),
          color: 'danger',
          position: 'top-right'
        })
      } finally {
        this.$vs.loading.close('.le-hero-identity > .con-vs-loading')
      }
    }
  }
}
</script>

<style scoped>
.le-view { 
  width: 100%; 
  display: flex; 
  flex-direction: column; 
  gap: 20px;

  .pages-sidebar.head {
    margin-right: 10px !important;
  }
  
  .table-title {
    p {
      font-size: 21px !important;
    }
  }
}

/* ─── Waiting banner ─── */
.le-pending-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #fff3e0;
  border-left: 4px solid #f57F17;
  color: #6a1f4c;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
}

/* ─── Top bar ─── */
.le-topbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
}

.le-save-btn ::v-deep span {
text-transform: none !important;
}

/* ─── Hero ─── */
.le-hero {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  align-items: stretch;
}

.le-hero-identity {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(46, 25, 55, 0.07);
  display: flex;
  flex-direction: column;
  position: relative;
}

.le-hero-photo-wrapper {
  position: relative;
  width: 100%;
  height: 160px;
  background-color: #e8d5f0;
}

.le-hero-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  opacity: 0;
  transition: opacity 0.3s ease;

  &--loaded {
    opacity: 1;
  }
}

.le-hero-photo-loading-skeleton {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #e8d5f0 25%, #f4e8f9 50%, #e8d5f0 75%);
  background-size: 200% 100%;
  animation: leHeroShimmer 1.5s infinite;
}

@keyframes leHeroShimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.le-cover-upload-trigger {
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(4px);
  border: 1px solid #e2d5ef;
  border-radius: 8px;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
}

.le-cover-upload-trigger:hover {
  background: #fff;
  border-color: #6a1f4c;
}

.le-cover-upload-trigger i {
  font-size: 18px;
  color: #6a1f4c;
}

.le-cover-upload-trigger span {
  font-size: 12px;
  font-weight: 600;
  color: #6a1f4c;
}

.le-hero-meta-container {
  display: flex;
  align-items: center;
  padding: 0 20px 16px;
  gap: 16px;
  background: #fff;
  justify-content: space-between;
}

.le-hero-network-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-left: auto;
  padding-top: 12px;
  flex-shrink: 0;
}

.le-hero-network-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 2px 8px rgba(46, 25, 55, 0.12);
}

.le-hero-logo-wrapper {
  position: relative;
  width: 72px;
  height: 72px;
  margin-top: -36px;
  border-radius: 50%;
  border: 4px solid #fff;
  box-shadow: 0 4px 12px rgba(46, 25, 55, 0.1);
  background: #fff;
  flex-shrink: 0;
  z-index: 5;
}

.le-hero-logo {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: #f3f0f7;
  background-size: cover;
  background-position: center;
}

.le-logo-upload-trigger {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #6a1f4c;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: background 0.15s;
  z-index: 6;
}

.le-logo-upload-trigger i {
  color: #fff;
  font-size: 14px;
}

.le-logo-upload-trigger:hover {
  background: #5a1840;
}

.le-hero-meta {
  padding-top: 12px;
  text-decoration: none !important;
  color: inherit !important;
}

.le-hero-meta[href] {
  cursor: pointer;
}

.le-hero-meta[href]:hover .le-hero-name {
  color: #6a1f4c;
  text-decoration: underline;
}

.le-hero-score {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(46, 25, 55, 0.07);
  padding: 24px 32px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 16px;
  /* min-width: 200px; */
}

.le-hero-score ::v-deep .cd-donut {
  width: 96px;
  height: 96px;
}

.le-hero-score ::v-deep .cd-donut::before {
  width: 64px;
  height: 64px;
}

.le-hero-score ::v-deep .cd-value {
  font-size: 20px;
  font-weight: 600;
  color: #B64985;
}

.le-hero-score-text {
  text-align: start;
}

.le-hero-score-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #121826;
}

.le-hero-score-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: #121826;
}

.le-hero-score-percent {
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #6a1f4c;
}

.le-hero-score-label {
  margin: 2px 0 0;
  font-size: 13px;
  color: #9e8dae;
}

.le-hero-score-badges {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  margin-top: 8px;
}

.le-hero-score-missing-wrapper,
.le-hero-score-pending-wrapper {
  position: relative;
  display: inline-flex;
}

.le-hero-score-missing,
.le-hero-score-pending {
  margin: 0;
  /* width: 205px; */
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease-in-out;
}

.le-hero-score-missing {
  color: #b64985;
  font-weight: 700;
  background: #fcf1f7;
  border: 1px solid #f3d4e6;
}

.le-hero-score-missing-wrapper:hover .le-hero-score-missing {
  background: #f8e1ee;
  border-color: #b64985;
  color: #6a1f4c;
}

.le-hero-score-pending {
  color: #92400e;
  font-weight: 600;
  background: #fff8eb;
  border: 1px solid #f5e2c2;
}

.le-hero-score-pending-wrapper:hover .le-hero-score-pending {
  background: #fef3c7;
  border-color: #d97706;
  color: #78350f;
}

.le-pending-icon {
  font-size: 14px;
  color: #d97706;
}

.le-missing-icon {
  font-size: 14px;
}

.le-score-popover {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 6px;
  width: 270px;
  background: #ffffff;
  /* border: 1.5px solid #e8daf0; */
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(46, 25, 55, 0.16);
  padding: 12px 14px;
  z-index: 100;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-4px);
  transition: all 0.2s ease-in-out;
}

.le-hero-score-missing-wrapper:hover .le-score-popover {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.le-score-popover-title {
  font-size: 11px;
  font-weight: 700;
  color: #6a1f4c;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.le-score-popover-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
}

.le-score-popover-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  border-radius: 6px;
  background: #fcf9fd;
  border: 1px solid #f3e9f7;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.le-score-popover-item:hover {
  background: #f5e9f7;
  border-color: #d9b3ca;
}

.le-score-popover-label {
  font-size: 11px;
  font-weight: 600;
  color: #2f1937;
  flex: 1;
}

.le-score-popover-weight {
  font-size: 10px;
  font-weight: 700;
  color: #6a1f4c;
  background: #f2e2ee;
  padding: 2px 6px;
  border-radius: 4px;
  margin: 0 6px;
}

.le-score-popover-btn {
  font-size: 10px;
  font-weight: 700;
  color: #6a1f4c;
}

/* ─── Content card wrapping tabs + content ─── */
.le-content-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(46, 25, 55, 0.07);
  overflow: visible;
}

/* ─── Tabs ─── */
.le-tabs {
  display: flex;
  background: #fff;
  border-bottom: 2px solid #f0e8f6;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.le-tabs::-webkit-scrollbar { display: none; }

button.le-tab {
  flex: 1;
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
  border: none !important;
  border-bottom: none !important;
  padding: 24px 20px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #9e8dae !important;
  cursor: pointer !important;
  transition: color 0.18s !important;
  min-width: 0 !important;
  white-space: nowrap !important;
  position: relative !important;
}

button.le-tab::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: transparent;
  transition: background 0.18s;
}

button.le-tab:hover {
  color: #6a1f4c !important;
  background-color: #faf5fd !important;
}

button.le-tab--active {
  color: #6a1f4c !important;
  font-weight: 700 !important;
}

button.le-tab--active::after {
  /* background: #6a1f4c; */
}

/* ─── Tab content ─── */
.le-tab-content {
  /* padding: 24px 28px; */
}

/* ─── Not found ─── */
.le-not-found {
  text-align: center;
  padding: 60px 20px;
  color: #9e8dae;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

/* ─── Facebook No Address Banner ─── */
.le-facebook-no-address-banner {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  background: #f8f2f6;
  border: 1px solid #e8dbe8;
  border-left: 5px solid #6a1f4c;
  border-radius: 12px;
  padding: 24px 28px;
  margin: 20px 20px;
}

.le-fb-no-addr-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.le-fb-no-addr-icon {
  font-size: 28px;
  color: #6a1f4c;
}

.le-fb-no-addr-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #6a1f4c;
}

.le-fb-no-addr-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #6a1f4c;
  max-width: 600px;
}

.le-fb-no-addr-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #6a1f4c;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.le-fb-no-addr-btn i {
  font-size: 18px;
}

.le-fb-no-addr-btn ::v-deep span {
  text-transform: none !important;
}

/* ─── Responsive ─── */
@media (max-width: 767px) {
  .le-hero { grid-template-columns: 1fr; }
  button.le-tab { flex: none; padding: 14px 16px !important; font-size: 13px !important; }
}
</style>
